import { Link } from "react-router-dom";
import { ArrowLeft, Zap } from "lucide-react";
import { LEGAL_UPDATED } from "@/data/legal";

const LOGO_CLASSES =
  "inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-red text-white";

function inline(text, keyPrefix) {
  return text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((chunk, i) =>
    chunk.startsWith("**") && chunk.endsWith("**") ? (
      <strong key={`${keyPrefix}-${i}`} className="font-semibold text-brand-ink">
        {chunk.slice(2, -2)}
      </strong>
    ) : (
      <span key={`${keyPrefix}-${i}`}>{chunk}</span>
    )
  );
}

function renderBlocks(markdown) {
  const lines = markdown.split("\n");
  const out = [];
  let bullets = [];

  const flushBullets = () => {
    if (!bullets.length) return;
    const items = bullets;
    bullets = [];
    out.push(
      <ul key={`ul-${out.length}`} className="mt-3 space-y-2 pl-5">
        {items.map((item, i) => (
          <li key={i} className="list-disc text-base leading-relaxed text-brand-mute">
            {inline(item, `li-${out.length}-${i}`)}
          </li>
        ))}
      </ul>
    );
  };

  lines.forEach((raw, idx) => {
    const line = raw.trim();
    if (!line) {
      flushBullets();
      return;
    }
    if (line.startsWith("- ")) {
      bullets.push(line.slice(2));
      return;
    }
    flushBullets();
    if (/^\*\*Last (updated|modified)/i.test(line)) return; // the shell already prints the date
    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      const level = heading[1].length;
      const body = heading[2];
      if (level === 1) return; // page title is rendered by the shell
      if (level === 2) {
        out.push(
          <h2
            key={`h2-${idx}`}
            className="mt-12 border-t border-border pt-8 font-heading text-lg font-bold tracking-tight text-brand-ink md:text-lg"
          >
            {inline(body, `h2-${idx}`)}
          </h2>
        );
      } else if (level === 3) {
        out.push(
          <h3 key={`h3-${idx}`} className="mt-8 font-heading text-base font-bold text-brand-navy">
            {inline(body, `h3-${idx}`)}
          </h3>
        );
      } else {
        out.push(
          <h4 key={`h4-${idx}`} className="mt-6 text-sm font-semibold uppercase tracking-wider text-brand-mute">
            {inline(body, `h4-${idx}`)}
          </h4>
        );
      }
      return;
    }
    out.push(
      <p key={`p-${idx}`} className="mt-4 text-base leading-relaxed text-brand-mute">
        {inline(line, `p-${idx}`)}
      </p>
    );
  });
  flushBullets();
  return out;
}

export function LegalDoc({ title, body, testId, otherLabel, otherTo }) {
  return (
    <div className="min-h-screen bg-brand-bg" data-testid={testId}>
      <header className="sticky top-0 z-20 border-b border-border bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-5 py-4 sm:px-8">
          <Link to="/" className="group flex items-center gap-2.5" data-testid="legal-brand-home">
            <span className={LOGO_CLASSES}>
              <Zap className="h-4 w-4" />
            </span>
            <span className="font-heading text-base font-bold tracking-tight text-brand-ink">
              TL-Thunderbird
            </span>
          </Link>
          <Link
            to={otherTo}
            className="text-sm font-medium text-brand-mute transition-colors hover:text-brand-red"
            data-testid="legal-switch-doc"
          >
            {otherLabel}
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 pb-24 pt-12 sm:px-8">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-mute transition-colors hover:text-brand-red"
          data-testid="legal-back-link"
        >
          <ArrowLeft className="h-4 w-4" /> Back to TL-Thunderbird
        </Link>

        <h1
          className="mt-6 font-heading text-4xl font-extrabold tracking-tight text-brand-ink sm:text-5xl"
          data-testid="legal-title"
        >
          {title}
        </h1>
        <p className="mt-3 text-sm text-brand-mute" data-testid="legal-updated">
          Last updated {LEGAL_UPDATED} · Throughline Politics, Inc.
        </p>

        <div className="mt-10" data-testid="legal-body">
          {renderBlocks(body)}
        </div>

        <p className="mt-16 border-t border-border pt-6 text-xs text-brand-mute">
          © 2026 Throughline Politics, Inc. All rights reserved.
        </p>
      </main>
    </div>
  );
}
